/*
 * portl-video-lazy.js
 * ──────────────────────────────────────────────────────────────────
 * Shared utility — viewport-gated video playback for autoplay
 * sections that are not above-the-fold.
 *
 * USAGE (in any section's HTML):
 *   <video
 *     class="my-video"
 *     muted loop playsinline webkit-playsinline
 *     preload="none"              ← critical: no bytes until play()
 *     data-portl-lazy-video        ← marker for this utility
 *     poster="..."                 ← optional, prevents black frame
 *   >
 *     <source src="..." type="video/mp4">
 *   </video>
 *
 * Then include the script ONCE per page (works for any section):
 *   <script src="{{ 'portl-video-lazy.js' | asset_url }}" defer></script>
 *
 * WHAT IT DOES:
 *   • Finds every [data-portl-lazy-video] element on the page
 *   • Observes them with IntersectionObserver (rootMargin: 200px,
 *     threshold: 0.25)
 *   • When approaching viewport → calls .play()
 *   • When leaving viewport     → calls .pause()
 *   • Far-off videos remain at preload="none" — zero network usage
 *
 * WHAT IT DOES NOT DO:
 *   • No CSS changes
 *   • No carousel coordination — sections handle their own slide
 *     logic. This is the BASE layer that says "only visible videos
 *     play."
 *
 * OPT-IN: FAR-OFF DECODER CLEANUP
 *   Add `data-portl-lazy-release` to the <video> element to also
 *   release decoder + buffered bytes when the video is truly far
 *   from the viewport (>1500px). Recommended only for sections
 *   with 10+ inline videos (e.g. reels carousels) where the
 *   cumulative memory cost matters. For 1-4 video sections it's
 *   not worth the brief blank frame on re-approach.
 *
 *   <video data-portl-lazy-video data-portl-lazy-release ...>
 *
 *   When the user scrolls back into view, the primary observer
 *   fires first (200px margin) and src is restored before play().
 *
 *   Manual control is also exposed:
 *     window.PortlVideoLazy.releaseDecoder(videoEl)
 *     window.PortlVideoLazy.restoreSrc(videoEl)
 *
 * BROWSER SUPPORT:
 *   • Modern browsers: native IntersectionObserver
 *   • Old browsers: graceful fallback — videos just play normally
 *     (autoplay attribute) without viewport gating. Page still works.
 *
 * ──────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.document) return;

  /* Feature detect — old browsers without IO just let videos
     autoplay normally. The autoplay attribute still works; we just
     don't pause them when they leave the viewport. That's fine
     because old browsers are rare and the perf cost is bounded. */
  if (!('IntersectionObserver' in window)) return;

  var IO_OPTIONS = {
    /* 200px rootMargin — start playing slightly BEFORE the video
       enters the viewport so the user doesn't see a frozen frame
       while bytes load. Tuned for typical scroll velocity on phones. */
    rootMargin: '200px 0px',
    /* threshold: 0.25 — play() fires when 25% of the video element
       overlaps the (expanded) viewport. Higher values would delay
       playback; lower values would over-trigger on tall videos. */
    threshold: 0.25
  };

  /* Track which videos we paused via this utility (vs. paused by the
     user / browser). We only resume the ones we paused. */
  var managedVideos = new WeakSet();

  function play(video) {
    if (!video) return;
    /* Only call play() if the video isn't already actively playing.
       Repeated play() during scroll throws spurious console warnings
       on some browsers. */
    if (!video.paused && !video.ended) return;
    var p = video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () {
        /* Autoplay rejected (rare for muted videos). Swallow — the
           native controls will be available if the section opted in. */
      });
    }
  }

  function pause(video) {
    if (!video) return;
    if (video.paused) return;
    try { video.pause(); } catch (e) {}
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var video = entry.target;
      if (entry.isIntersecting) {
        play(video);
        /* Approaching viewport — if we previously released this
           video's src for memory, restore it now so playback can
           start when it enters view. */
        if (video.dataset.portlLazyReleased === '1') restoreSrc(video);
      } else {
        pause(video);
      }
    });
  }, IO_OPTIONS);

  /* ──────────────────────────────────────────────────────────────
     FAR-OFF CLEANUP — opt-in via [data-portl-lazy-release]

     For pages with MANY videos (e.g. a 16-card reels carousel), the
     decoder + buffer cost of paused-but-loaded videos adds up. With
     this attribute the script also runs a SECOND observer with a
     much larger rootMargin (1500px). When a video moves outside
     that wide zone, its <source src> attributes are removed and
     load() is called — releasing the video decoder and any
     buffered bytes back to the browser.

     When the user scrolls back, the primary observer fires first
     (at 200px rootMargin) and restoreSrc() brings src back, then
     play() runs. There's a brief blank frame while bytes re-fetch
     (similar to the initial cold load), but RAM usage stays flat
     even across long pages of inline videos.

     DEFAULT IS OFF — most sections have 1–4 videos and don't need
     this. Only add the attribute when the section renders 10+
     inline videos on a single page.
     ────────────────────────────────────────────────────────────── */
  var FAR_MARGIN = '1500px 0px';

  function releaseDecoder(video) {
    if (video.dataset.portlLazyReleased === '1') return;
    var sources = video.querySelectorAll('source');
    Array.prototype.forEach.call(sources, function (s) {
      var url = s.getAttribute('src');
      if (url) {
        s.setAttribute('data-portl-saved-src', url);
        s.removeAttribute('src');
      }
    });
    var vSrc = video.getAttribute('src');
    if (vSrc) {
      video.setAttribute('data-portl-saved-src', vSrc);
      video.removeAttribute('src');
    }
    try { video.load(); } catch (e) {}
    video.dataset.portlLazyReleased = '1';
  }

  function restoreSrc(video) {
    if (video.dataset.portlLazyReleased !== '1') return;
    var sources = video.querySelectorAll('source');
    Array.prototype.forEach.call(sources, function (s) {
      var url = s.getAttribute('data-portl-saved-src');
      if (url) {
        s.setAttribute('src', url);
        s.removeAttribute('data-portl-saved-src');
      }
    });
    var vSaved = video.getAttribute('data-portl-saved-src');
    if (vSaved) {
      video.setAttribute('src', vSaved);
      video.removeAttribute('data-portl-saved-src');
    }
    try { video.load(); } catch (e) {}
    delete video.dataset.portlLazyReleased;
  }

  var farObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var video = entry.target;
      if (!entry.isIntersecting) {
        /* Outside the 1500px far zone → release decoder + bytes. */
        releaseDecoder(video);
      } else {
        /* Inside the 1500px zone → restore src if we previously
           released it, so the video is ready to play when the
           section's own near observer (if any) calls play().

           This is symmetric — without this, sections that ONLY opt
           into data-portl-lazy-release (e.g. reels, which have their
           own near observer) would have their src removed when the
           user scrolls far past, then never restored when they
           scroll back. The video element would silently fail to
           autoplay because it has no source. */
        if (video.dataset.portlLazyReleased === '1') restoreSrc(video);
      }
    });
  }, { rootMargin: FAR_MARGIN, threshold: 0 });

  function attach(video) {
    if (managedVideos.has(video)) return;
    managedVideos.add(video);
    /* Near observer (200px) only attaches if data-portl-lazy-video is
       set. Sections that have their OWN viewport play/pause logic
       (e.g. portl-social-reels) can skip this and use only the
       far-off release by setting data-portl-lazy-release alone. */
    if (video.hasAttribute('data-portl-lazy-video')) {
      observer.observe(video);
    }
    /* Far observer (1500px) attaches when data-portl-lazy-release is
       set. Works independently of the near observer — useful for
       sections that handle play/pause themselves but still want
       decoder cleanup on far-away videos. */
    if (video.hasAttribute('data-portl-lazy-release')) {
      farObserver.observe(video);
    }
  }

  function scan(root) {
    root = root || document;
    /* Either attribute activates the script. data-portl-lazy-video
       is the standard lazy gate; data-portl-lazy-release is the
       opt-in memory cleanup (works alone or alongside the gate). */
    var nodes = root.querySelectorAll('[data-portl-lazy-video], [data-portl-lazy-release]');
    Array.prototype.forEach.call(nodes, attach);
  }

  /* Initial scan once DOM is parsed. The `defer` attribute on the
     <script> tag means this runs after HTML parse but before window
     load, which is the sweet spot. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  /* Re-scan when Shopify theme editor re-renders a section. The
     editor fires `shopify:section:load` on the section root, and
     the new DOM is a descendant of that root. */
  document.addEventListener('shopify:section:load', function (e) {
    if (e && e.target) scan(e.target);
  });

  /* Public API for sections that dynamically add videos (e.g.
     carousels that mount slides on demand). Sections can call:
       window.PortlVideoLazy.attach(videoEl)
     after adding the video to the DOM. */
  window.PortlVideoLazy = {
    attach: attach,
    scan: scan,
    /* Advanced — manual far-off cleanup for sections that want to
       trigger release/restore based on their own logic (e.g. a
       carousel that knows which slides are far from the viewport). */
    releaseDecoder: releaseDecoder,
    restoreSrc: restoreSrc
  };
})();
